import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import {
  IAutoCompleteOnFetchEnd,
  IAutoCompleteOnSet,
} from '../../../shared/directives/auto-complete/auto-complete.interface';
import {
  I2gNgTrackingActionBuilder,
  ITrackingProperties,
} from 'i2g-ng-tracking';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { FormAlignmentsEnum } from '../../form-master.enum';
import { FormFieldComponent } from '../form-field/form-field.component';
import { filter, takeUntil } from 'rxjs/operators';
import { IFormFieldErrorMessages } from '../../form-master.interface';

@Component({
  selector: 'app-input-field',
  templateUrl: 'input-field.component.html',
  styleUrls: ['input-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputFieldComponent
  extends FormFieldComponent
  implements OnChanges, OnDestroy {
  @Input() alignment: FormAlignmentsEnum;
  @Input() label: string;
  @Input() hideLabel: boolean;
  @Input() fieldId: string;
  @Input() description: string;
  @Input() condensed: boolean;
  @Input() placeholder: string;
  @Input() fieldControl: AbstractControl;
  @Input() leftIcon: IIcon;
  @Input() rightIcon: IIcon;
  @Input() type = 'text';
  @Input() options: any;
  @Input() focusableTrigger$: Observable<void>;
  @Input() maxlength = -1;
  @Input() e2eId: string;
  @Input() errorMessages: IFormFieldErrorMessages;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingAction: ITrackingAction;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() trackingAdditionalProperties: ITrackingProperties;

  @Output() onFocus: EventEmitter<string> = new EventEmitter<string>();
  @Output() onBlur: EventEmitter<string> = new EventEmitter<string>();
  @Output() onRightActionClick: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  onInputKeyup: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();
  @Output()
  onAutoCompleteFetchStart: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  onAutoCompleteFetchEnd: EventEmitter<IAutoCompleteOnFetchEnd> = new EventEmitter<IAutoCompleteOnFetchEnd>();
  @Output()
  onAutoCompleteSet: EventEmitter<IAutoCompleteOnSet> = new EventEmitter<IAutoCompleteOnSet>();
  @Output() onAutoCompleteHide: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  onAutoCompleteDestroy: EventEmitter<void> = new EventEmitter<void>();

  private _fieldSub: Subscription;

  constructor(
    changeDetectorRef: ChangeDetectorRef,
    translateService: TranslateService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingDispatcher: TrackingDispatcher
  ) {
    super(changeDetectorRef, translateService);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.fieldControl ||
        changes.trackingObject ||
        changes.trackingScreenName) &&
      this.fieldControl &&
      this.trackingObject &&
      this.trackingScreenName
    ) {
      if (this._fieldSub) {
        this._fieldSub.unsubscribe();
      }

      this._fieldSub = this.fieldControl.statusChanges
        .pipe(
          takeUntil(this.destroyed),
          filter(() => this.fieldControl.invalid)
        )
        .subscribe((_) => {
          const errors = this.fieldControl.errors;
          let errorTrackingAction: ITrackingAction;

          if (errors.required) {
            errorTrackingAction = this._trackingActionBuilder.buildErrorInvoked(
              'field_not_populated',
              this.fieldId,
              this.trackingScreenName
            );
          } else if (errors.maxlength) {
            errorTrackingAction = this._trackingActionBuilder.buildErrorInvoked(
              'field_length_exceeded',
              this.fieldId,
              this.trackingScreenName
            );
          }

          if (errorTrackingAction) {
            this._trackingDispatcher.trackEvent(
              this.trackingObject,
              errorTrackingAction,
              this.trackingAdditionalProperties
            );
          }
        });
    }

    super.ngOnInit();
  }

  ngOnDestroy() {
    if (this._fieldSub) {
      this._fieldSub.unsubscribe();
    }

    super.ngOnDestroy();
  }
}
